import { clients } from '../clients/grpcClients';
import {
  CreateProjectHierarchyRequest,
  CreateProjectHierarchyResponse,
  CreateProjectRequest,
  CreateProjectResponse,
  DeleteProjectHierarchyRequest,
  DeleteProjectHierarchyResponse,
  GetProjectHierarchyRequest,
  GetProjectHierarchyResponse,
  GetProjectsRequest,
  GetProjectsResponse,
  Project,
  ProjectHierarchy,
  UpdateProjectHierarchyRequest,
  UpdateProjectHierarchyResponse,
  UpdateProjectRequest,
  UpdateProjectResponse,
} from './protosCompiled/project/project_pb';
import { useLocalStorage } from '../stores/localStorage';

export class ProjectService {
  private static getMetadata() {
    const sessionToken = useLocalStorage.getState().sessionToken;
    if (!sessionToken) {
      console.error('Session token is missing!');
    }
    return {
      sessionToken,
    };
  }
  //gRPC API
  public static createProject = (
    data: Project.AsObject
  ): Promise<CreateProjectResponse.AsObject> => {
    const request = new Project();
    request.setName(data.name);
    request.setAddress(data.address);
    request.setDescription(data.description);
    request.setStartdate(data.startdate);
    request.setEnddate(data.enddate);
    request.setProjecthierarchyid(data.projecthierarchyid);

    const req = new CreateProjectRequest();
    req.setSessiontoken(this.getMetadata().sessionToken);
    req.setProject(request);

    return new Promise((resolve, reject) => {
      clients.projectServiceClient.createProject(req, {}, (err, response) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(response.toObject()); // Convert the protobuf response to a plain object
      });
    });
  };

  public static fetchProjects = (): Promise<GetProjectsResponse.AsObject> => {
    return new Promise((resolve, reject) => {
      const request = new GetProjectsRequest();
      request.setSessiontoken(this.getMetadata().sessionToken);
      clients.projectServiceClient.getProjects(request, {}, (err, response) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(response.toObject());
      });
    });
  };

  public static updateProject = (
    data: Project.AsObject
  ): Promise<UpdateProjectResponse.AsObject> => {
    const project = new Project();
    project.setId(data.id);
    project.setName(data.name);
    project.setAddress(data.address);
    project.setDescription(data.description);
    project.setStartdate(data.startdate);
    project.setEnddate(data.enddate);
    project.setProjecthierarchyid(data.projecthierarchyid);

    const req = new UpdateProjectRequest();
    req.setSessiontoken(this.getMetadata().sessionToken || ''); // Ensure token is passed
    req.setProject(project); // Attach the Project object

    return new Promise((resolve, reject) => {
      clients.projectServiceClient.updateProject(req, {}, (err, response) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(response.toObject());
      });
    });
  };

  // Project Hierarchy methods
  public static createProjectHierarchy = (data: {
    parentid?: string;
    code: string;
    name: string;
    description?: string;
    isactive?: boolean;
  }): Promise<CreateProjectHierarchyResponse.AsObject> => {
    const projectHierarchy = new ProjectHierarchy();
    // Only set fields that should be sent to backend
    projectHierarchy.setParentid(data.parentid || '');
    projectHierarchy.setCode(data.code);
    projectHierarchy.setName(data.name);
    projectHierarchy.setDescription(data.description || '');
    projectHierarchy.setIsactive(data.isactive ?? true);
    // Do NOT set path, createdat, updatedat, createdbyuserid, createdbyaccountid
    // Backend will compute/fill these

    const req = new CreateProjectHierarchyRequest();
    req.setSessiontoken(this.getMetadata().sessionToken);
    req.setProjecthierarchy(projectHierarchy);

    return new Promise((resolve, reject) => {
      clients.projectServiceClient.createProjectHierarchy(req, {}, (err, response) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(response.toObject());
      });
    });
  };

  public static getProjectHierarchy = (): Promise<GetProjectHierarchyResponse.AsObject> => {
    return new Promise((resolve, reject) => {
      const request = new GetProjectHierarchyRequest();
      request.setSessiontoken(this.getMetadata().sessionToken);
      clients.projectServiceClient.getProjectHierarchy(request, {}, (err, response) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(response.toObject());
      });
    });
  };

  public static updateProjectHierarchy = (data: {
    id: string;
    parentid: string;
    code: string;
    name: string;
    description: string;
    isactive: boolean;
  }): Promise<UpdateProjectHierarchyResponse.AsObject> => {
    const projectHierarchy = new ProjectHierarchy();
    // Send all mutable fields (PUT semantics)
    projectHierarchy.setId(data.id);
    projectHierarchy.setParentid(data.parentid);
    projectHierarchy.setCode(data.code);
    projectHierarchy.setName(data.name);
    projectHierarchy.setDescription(data.description);
    projectHierarchy.setIsactive(data.isactive);
    // Do NOT set path, createdat, updatedat, createdbyuserid, createdbyaccountid
    // Backend will compute/fill these

    const req = new UpdateProjectHierarchyRequest();
    req.setSessiontoken(this.getMetadata().sessionToken);
    req.setProjecthierarchy(projectHierarchy);

    return new Promise((resolve, reject) => {
      clients.projectServiceClient.updateProjectHierarchy(req, {}, (err, response) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(response.toObject());
      });
    });
  };

  public static deleteProjectHierarchy = (
    id: string
  ): Promise<DeleteProjectHierarchyResponse.AsObject> => {
    const req = new DeleteProjectHierarchyRequest();
    req.setId(id);
    req.setSessiontoken(this.getMetadata().sessionToken);

    return new Promise((resolve, reject) => {
      clients.projectServiceClient.deleteProjectHierarchy(req, {}, (err, response) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(response.toObject());
      });
    });
  };
}
